import { useState, useEffect } from "react";
import { Users, CloudUpload, CheckCircle, AlertCircle, Clock, HardDrive, TrendingUp, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, icon: Icon, color = "#c6a96f" }) {
  return (
    <div className="ug-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="section-label text-[9px]">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <p className="text-2xl font-bold text-[#f4f4f5] font-mono">{value}</p>
      {sub && <p className="text-[10px] text-[#52525b] mt-1">{sub}</p>}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "0 GB";
  const gb = bytes / (1024 ** 3);
  if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`;
  return `${gb.toFixed(1)} GB`;
}

export default function AdminPanel({ onBack }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [statsRes, usersRes, activityRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/users`),
          axios.get(`${API}/admin/activity`),
        ]);
        if (statsRes.data.error) { setError(statsRes.data.error); return; }
        setStats(statsRes.data);
        setUsers(usersRes.data.users || []);
        setActivity(activityRes.data.daily || []);
      } catch (err) {
        setError("Failed to load admin data");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-24">
        <Clock className="h-6 w-6 animate-spin text-[#c6a96f]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <AlertCircle className="h-8 w-8 text-[#c47272]" />
        <p className="text-sm text-[#c47272]">{error}</p>
        <button onClick={onBack} className="btn-ghost px-4 py-2 rounded-xl text-xs">Back to Dashboard</button>
      </div>
    );
  }

  const s = stats || {};
  const bw = s.bandwidth || {};
  const byStatus = s.transfers_by_status || {};

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={onBack} className="text-[#52525b] hover:text-[#c6a96f] transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="section-label mb-1">// admin</div>
          <h1 className="text-2xl font-bold text-[#f4f4f5]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Command Center
          </h1>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="// total users" value={s.total_users || 0} sub={`${s.recent_signups_30d || 0} in last 30 days`} icon={Users} />
        <StatCard label="// total transfers" value={s.total_transfers || 0} sub={`${s.completed_today || 0} completed today`} icon={CloudUpload} />
        <StatCard label="// completed" value={byStatus.completed || 0} sub={`${byStatus.failed || 0} failed · ${byStatus.pending || 0} pending`} icon={CheckCircle} color="#5fa87a" />
        <StatCard label="// bandwidth" value={formatBytes(bw.total_bytes)} sub={`${bw.avg_file_mb || 0} MB avg · ${bw.completed_count || 0} files`} icon={HardDrive} />
      </div>

      {/* Destination breakdown */}
      {s.by_destination && Object.keys(s.by_destination).length > 0 && (
        <div className="ug-card p-5 mb-8">
          <div className="section-label mb-3">// by destination</div>
          <div className="flex gap-6">
            {Object.entries(s.by_destination).map(([dest, count]) => (
              <div key={dest} className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: dest === "gdrive" ? "#5ba89d" : "#c6a96f" }} />
                <span className="text-sm text-[#a1a1aa] font-mono">{dest === "gdrive" ? "Google Drive" : "Dropbox"}: {count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity chart (simple bar visualization) */}
      {activity.length > 0 && (
        <div className="ug-card p-5 mb-8">
          <div className="section-label mb-4">// last 14 days</div>
          <div className="flex items-end gap-1 h-32">
            {activity.map((day, i) => {
              const maxVal = Math.max(...activity.map(d => d.completed), 1);
              const height = (day.completed / maxVal) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col justify-end" style={{ height: '100px' }}>
                    <div
                      className="w-full rounded-t-sm transition-all"
                      style={{ height: `${Math.max(height, 2)}%`, backgroundColor: '#c6a96f', opacity: height > 0 ? 1 : 0.2 }}
                      title={`${day.date}: ${day.completed} completed, ${day.signups} signups`}
                    />
                  </div>
                  <span className="text-[8px] text-[#52525b] font-mono">{day.date.split(' ')[1]}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-[#c6a96f]" />
              <span className="text-[10px] text-[#52525b]">Completed transfers</span>
            </div>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="ug-card p-5">
        <div className="section-label mb-4">// users ({users.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#52525b] font-mono border-b border-[#222630]">
                <th className="pb-2 pr-4">Email</th>
                <th className="pb-2 pr-4">Joined</th>
                <th className="pb-2 pr-4">Vimeo</th>
                <th className="pb-2 pr-4">Dropbox</th>
                <th className="pb-2 pr-4">GDrive</th>
                <th className="pb-2 pr-4 text-right">Transfers</th>
                <th className="pb-2 pr-4 text-right">Done</th>
                <th className="pb-2 text-right">Failed</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i} className="border-b border-[#1a1d21] hover:bg-[#21252b] transition-colors">
                  <td className="py-2.5 pr-4 text-[#e4e4e7] truncate max-w-[180px]">{u.email}</td>
                  <td className="py-2.5 pr-4 text-[#52525b] font-mono">{u.created_at?.slice(0, 10) || "—"}</td>
                  <td className="py-2.5 pr-4">{u.vimeo ? <span className="text-[#5fa87a]">{u.vimeo}</span> : <span className="text-[#3f3f46]">—</span>}</td>
                  <td className="py-2.5 pr-4">{u.dropbox ? <span className="text-[#5fa87a]">{u.dropbox}</span> : <span className="text-[#3f3f46]">—</span>}</td>
                  <td className="py-2.5 pr-4">{u.gdrive ? <span className="text-[#5fa87a]">{u.gdrive}</span> : <span className="text-[#3f3f46]">—</span>}</td>
                  <td className="py-2.5 pr-4 text-right font-mono text-[#a1a1aa]">{u.transfers}</td>
                  <td className="py-2.5 pr-4 text-right font-mono text-[#5fa87a]">{u.completed}</td>
                  <td className="py-2.5 text-right font-mono text-[#c47272]">{u.failed || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
